import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { AlertCircle, CheckCircle, Shield, RefreshCw, Loader, ExternalLink } from 'lucide-react';
import bitdefenderService from '../../services/bitdefenderService';
import { toast } from 'sonner';

export default function BitDefenderEndpointDialog({ endpointId, open, onOpenChange }) {
  const [endpoint, setEndpoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);

  const loadEndpointData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await bitdefenderService.getEndpointStatus(endpointId);
      if (data && data.endpoint) {
        setEndpoint(data.endpoint);
      }
    } catch (error) {
      console.error('Error loading endpoint:', error);
      toast.error('Failed to load endpoint details');
    } finally {
      setLoading(false);
    }
  }, [endpointId]);

  useEffect(() => {
    if (open && endpointId) {
      loadEndpointData();
    }
  }, [open, endpointId, loadEndpointData]);

  const handleQuickScan = async () => {
    setScanning(true);
    try {
      const result = await bitdefenderService.triggerScan(endpointId, 1);
      if (result.success) {
        toast.success('Quick scan initiated');
        await loadEndpointData();
      }
    } catch (error) {
      toast.error('Failed to initiate scan');
    } finally {
      setScanning(false);
    }
  };

  const handleFullScan = async () => {
    setScanning(true);
    try {
      const result = await bitdefenderService.triggerScan(endpointId, 2);
      if (result.success) {
        toast.success('Full scan initiated');
        await loadEndpointData();
      }
    } catch (error) {
      toast.error('Failed to initiate scan');
    } finally {
      setScanning(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'protected':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'at_risk':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'infected':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Shield className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'protected':
        return 'bg-green-100 text-green-800';
      case 'at_risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'infected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {endpoint && getStatusIcon(endpoint.security_status)}
              <div>
                <DialogTitle>{endpoint?.endpoint_name || 'Loading...'}</DialogTitle>
                <p className="text-sm text-gray-600">{endpoint?.fqdn}</p>
              </div>
            </div>
            <Badge className={getStatusColor(endpoint?.security_status)}>
              {(endpoint?.security_status || 'unknown').toUpperCase()}
            </Badge>
          </div>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="w-6 h-6 animate-spin" />
          </div>
        ) : (
          <Tabs defaultValue="overview" className="mt-4">
            <div className="overflow-x-auto">
              <TabsList className="inline-flex w-auto min-w-full lg:grid lg:grid-cols-3">
                <TabsTrigger value="overview" className="whitespace-nowrap">Overview</TabsTrigger>
                <TabsTrigger value="security" className="whitespace-nowrap">Security Details</TabsTrigger>
                <TabsTrigger value="actions" className="whitespace-nowrap">Actions</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="overview" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Endpoint Information</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Endpoint Name</p>
                    <p className="font-semibold">{endpoint?.endpoint_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">FQDN</p>
                    <p className="font-semibold">{endpoint?.fqdn || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Endpoint ID</p>
                    <p className="text-sm font-mono">{endpoint?.endpoint_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Last Synced</p>
                    <p className="text-sm">
                      {endpoint?.synced_at ? new Date(endpoint.synced_at).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="security" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Antimalware Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge variant={endpoint?.antimalware_enabled ? 'default' : 'destructive'}>
                      {endpoint?.antimalware_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Product Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge variant={endpoint?.product_outdated ? 'destructive' : 'default'}>
                      {endpoint?.product_outdated ? 'Outdated' : 'Current'}
                    </Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">License Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Badge variant={endpoint?.license_valid ? 'default' : 'destructive'}>
                      {endpoint?.license_valid ? 'Valid' : 'Expired'}
                    </Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Threats Detected</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">{endpoint?.threat_count || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {endpoint?.last_scan_date && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Last Scan</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p>{new Date(endpoint.last_scan_date).toLocaleString()}</p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="actions" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Scan Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    onClick={handleQuickScan} 
                    disabled={scanning}
                    className="w-full"
                  >
                    {scanning ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      'Quick Scan'
                    )}
                  </Button>
                  
                  <Button 
                    onClick={handleFullScan} 
                    disabled={scanning}
                    variant="outline"
                    className="w-full"
                  >
                    Full System Scan
                  </Button>

                  <Button 
                    onClick={loadEndpointData}
                    disabled={loading || scanning}
                    variant="ghost"
                    className="w-full"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Refresh Data
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
